<template>
  <form @submit.prevent="submit">
    <naj-box :title="local.bankruptcy.basicDataDetails.title">
      <v-progress-linear
        v-if="isMainRequestLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-row>
        <v-col
          cols="12"
          lg="4"
        >
          <v-row class="mt-lg-5 mt-md-0">
            <v-col cols="12">
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.bankruptcy.basicDataDetails.legalCapacity"
                :items="selectableLegalCapacities"
                :value.sync="mainRequest.legalCapacityId"
                item-value="id"
                item-text="name"
                validates="required"
                :disabled="isEditMode || isViewMode || isMainRequestLoading || selectableLegalCapacities.length == 1"
                @change="legalCapacityChange"
              ></naj-input>
            </v-col>
            <v-col cols="12">
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.bankruptcy.basicDataDetails.requestOwnerType"
                :items="selectableRequestOwnerTypes"
                :value.sync="mainRequest.requestOwnerTypeId"
                item-value="id"
                item-text="name"
                :disabled="isEditMode || isViewMode || isMainRequestLoading"
                validates="required"
                @change="requestOwnerTypeChanged"
              ></naj-input>
            </v-col>
            <v-col cols="12">
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="procedureTypeLabel"
                :items="selectableProcedureTypes"
                :value.sync="mainRequest.procedureTypeId"
                :disabled="isViewMode || isMainRequestLoading || (auditConfirmations[`flag_${mainRequestAuditFlag.basicDetailsConfirmation}`] && isNotCompleted)"
                item-value="id"
                item-text="name"
                validates="required"
              ></naj-input>
            </v-col>
            <v-col cols="12">
              <naj-input
                :type-id="formFieldType.textarea"
                :label="local.bankruptcy.basicDataDetails.requestReason"
                :value.sync="mainRequest.requestReason"
                :disabled="isViewMode || isMainRequestLoading || (auditConfirmations[`flag_${mainRequestAuditFlag.basicDetailsConfirmation}`] && isNotCompleted)"
                validates="required|max:1000"
              ></naj-input>
              <audit-confirmation-flag-checkbox :audit-flag="mainRequestAuditFlag.basicDetailsConfirmation"></audit-confirmation-flag-checkbox>
            </v-col>
            <v-col cols="12">
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.bankruptcy.basicDataDetails.commercialCourt"
                :items="commercialCourts"
                :value.sync="mainRequest.courtId"
                item-value="id"
                :disabled="isViewMode || isMainRequestLoading || (auditConfirmations[`flag_${mainRequestAuditFlag.specializedCourtConfirmation}`] && isNotCompleted)"
                item-text="name"
                validates="required"
              ></naj-input>
              <audit-confirmation-flag-checkbox :audit-flag="mainRequestAuditFlag.specializedCourtConfirmation"></audit-confirmation-flag-checkbox>
            </v-col>

            <v-col cols="12">
              <naj-input
                :type-id="formFieldType.checkbox"
                :value="isPledged"
                :label="local.bankruptcy.pledge.pledgeLabel"
                class="inline-label primary-label-color pledged"
                :disabled="!isNewMode "
                @change="pledgedChange"
              ></naj-input>
              <div
                v-if="submitted && !isPledged"
                class="error--text"
              >
                {{ local.bankruptcy.pledge.pledgeErrorText }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="description-border">
          <p class="title-text">
            {{ local.bankruptcy.hint.section1Title }}
          </p>

          <p class="description-text">
            {{ local.bankruptcy.hint.section1 }}
          </p>

          <p class="title-text">
            {{ local.bankruptcy.hint.section2Title }}
          </p>
          <div class="hint-text">
            <li type="1">
              {{ local.bankruptcy.hint.section2_1 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section2_2 }}
            </li>

            <li type="1">
              {{ local.bankruptcy.hint.section2_3 }}
            </li>
            <div class="child-list">
              <li>{{ local.bankruptcy.hint.section2_3_1 }}</li>

              <li> {{ local.bankruptcy.hint.section2_3_2 }}</li>

              <li>{{ local.bankruptcy.hint.section2_3_3 }}</li>

              <li> {{ local.bankruptcy.hint.section2_3_4 }}</li>

              <li> {{ local.bankruptcy.hint.section2_3_5 }}</li>
            </div>
            <li type="1">
              {{ local.bankruptcy.hint.section2_4 }}
            </li>
          </div>
          <p class="title-text">
            {{ local.bankruptcy.hint.section3Title }}
          </p>
          <div class="required-attachments-text">
            <li type="1">
              {{ local.bankruptcy.hint.section3_1 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_2 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_3 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_4 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_5 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_6 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_7 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_8 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_9 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_10 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_11 }}
            </li>
            <li type="1">
              {{ local.bankruptcy.hint.section3_12 }}
            </li>
          </div>
        </v-col>
      </v-row>
    </naj-box>

    <naj-box
      v-if="mainRequest.legalCapacityId"
      :title="local.bankruptcy.requestApplicant.title"
    >
      <request-applicant
        :form-model="requestApplicant"
        :legal-capacity-id="mainRequest.legalCapacityId"
        :audit-confirmations="auditConfirmations"
        :is-view-mode="isViewMode"
        :is-new-mode="isNewMode"
        :is-edit-mode="isEditMode"
        :is-not-completed="isNotCompleted"
      ></request-applicant>
    </naj-box>

    <v-row>
      <v-col
        cols="
          12"
        class="text-left"
      >
        <naj-button
          type="submit"
          :disabled="isMainRequestLoading"
        >
          {{ local.common.next }}
        </naj-button>
      </v-col>
    </v-row>
  </form>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
const { mapActions: mapSysActions } = createNamespacedHelpers('SystemModule');
const { mapState, mapState: mapLUTState, mapActions, mapGetters } = createNamespacedHelpers(
  'BankruptcyModule'
);
import {
  stepsEnum,
  requestOwnerTypeEnum,
  mainRequestCategoryEnum,
  procedureTypeEnum,
  legalCapacitiesEnum,
  mainRequestAuditFlag,
  requestStatusEnum,
} from '@/modules/bankruptcy/helpers/enum';
import { scrollIntoInValidInput } from '@/helpers/utils';
import RequestApplicant from './request-applicant/index.vue';
import { mode, userType } from '@/helpers/enum';
import AuditConfirmationFlagCheckbox from '@/modules/bankruptcy/components/request/audit-confirmation-flag-checkbox';
const { mapActions: mapGlobalActions, mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');

export default {
  name: 'BasicData',
  components: {
    RequestApplicant,
    AuditConfirmationFlagCheckbox,
  },
  data() {
    return {
      stepsEnum,
      userType,
      requestOwnerTypeEnum,
      mainRequestCategoryEnum,
      procedureTypeEnum,
      legalCapacitiesEnum,
      mainRequestAuditFlag,
      submitted: false,
      requestStatusEnum,
    };
  },
  computed: {
    ...mapGlobalState(['userInfo']),
    ...mapState({
      isMainRequestLoading: state => state.isMainRequestLoading,
      mainRequest: state => state.mainRequest,
      requestApplicant: state => state.requestApplicant,
      auditConfirmations: state => state.auditConfirmations,
    }),
    requestObjectKey() {
      return this.$route.params.objectKey;
    },
    isSecretary() {
      return this.mainRequest.legalCapacityId == legalCapacitiesEnum.bankruptcySecretary;
    },
    isLawyer() {
      return this.mainRequest.legalCapacityId == legalCapacitiesEnum.lawyer;
    },
    procedureTypeLabel() {
      const suffix = this.isSecretary ? 'procedureTypeDeposit' : 'procedureTypeOpen';
      return this.local.bankruptcy.basicDataDetails[suffix];
    },
    selectableRequestOwnerTypes() {
      const requestOwnerTypes = this.requestOwnerTypes.filter(lc => lc.id != requestOwnerTypeEnum.stakeholder && lc.id != requestOwnerTypeEnum.bankruptcyCommission);
      if (this.isSecretary) {
        const secretaryOwnerTypes = [requestOwnerTypeEnum.debtor, requestOwnerTypeEnum.competentParty];
        return requestOwnerTypes.filter(ot => secretaryOwnerTypes.some(s => s == ot.id));
      }
      return requestOwnerTypes;
    },
    selectableLegalCapacities() {
      if (!this.userType) return [];

      let allowedLegalCapacities = [];
      if (this.userInfo.userType === this.userType.najizCitizen) {
        allowedLegalCapacities = [
          legalCapacitiesEnum.bankruptcySecretary,
          legalCapacitiesEnum.administrativeEntityRepresentative,
          legalCapacitiesEnum.legalEntityRepresentative,
        ];
        if (!this.isNewMode)
          allowedLegalCapacities.push(legalCapacitiesEnum.lawyer);
      }
      else if (this.userInfo.userType === this.userType.najizLawyer) {
        allowedLegalCapacities = [
          legalCapacitiesEnum.lawyer,
        ];
        this.$set(this.mainRequest, 'legalCapacityId', legalCapacitiesEnum.lawyer);
        this.$set(this.mainRequest, 'categoryId', mainRequestCategoryEnum.open);
      }
      return this.legalCapacities.filter(ot => allowedLegalCapacities.some(s => s == ot.id));
    },
    selectableProcedureTypes() {
      if (!this.mainRequest.legalCapacityId || !this.mainRequest.requestOwnerTypeId) { return []; }
      return this.getSelectableProcedureTypes();
    },
    ...mapGetters([
      'isViewMode',
      'isNewMode',
      'isEditMode',
      'isNotCompleted',
    ]),
    ...mapLUTState([
      'mainRequestCategories',
      'requestOwnerTypes',
      'legalCapacities',
      'commercialCourts',
      'procedureTypes',
      'isPledged',
    ]),
  },
  mounted() {
    if (!this.isNewMode) {
      this.submitted = true;
    }
    Promise.all([
      this.getUserInfo(),
      this.getMainRequestCategories(),
      this.getRequestOwnerTypes(),
      this.getLegalCapacities(),
      this.getCommercialCourts(),
      this.getProcedureTypes(),
    ])
      .then(() => { });
  },
  methods: {
    ...mapActions([
      'getMainRequestCategories',
      'getRequestOwnerTypes',
      'getLegalCapacities',
      'getCommercialCourts',
      'getProcedureTypes',
      'updateIsPledged',
      'submitMainRequest',
      'updateMainRequest',
      'openRequest',
    ]),
    ...mapSysActions([
      'validateLawyer',
    ]),
    ...mapGlobalActions(['getUserInfo']),
    pledgedChange(value) {
      this.submitted = false;
      this.updateIsPledged(value);
    },
    save() {
      const model = {
        ...this.mainRequest,
        applicantDetails: {
          ...this.requestApplicant,
          legalCapacityId: this.mainRequest.legalCapacityId,
          requestOwnerTypeId: this.mainRequest.requestOwnerTypeId,
        },
        requestObjectKey: this.requestObjectKey,
        mode: this.requestObjectKey ? mode.update : mode.new,
      };
      this[this.requestObjectKey ? 'updateMainRequest' : 'submitMainRequest'](model)
        .then((data) => {
          let url = `/bankruptcy/request/2/${data}`;
          this.$router.push({ path: url });
        })
        .finally(() => (this.loading = false));
    },
    submit() {
      if (this.isNewMode || this.isEditMode) {
        this.$validator.validateAll().then((isValid) => {
          this.submitted = true;
          if (isValid && this.isPledged) {
            if (this.isLawyer) {
              this.validateLawyer({
                socialId: this.requestApplicant.identityNumber,
                isTraineeLawyer: false,
              })
                .then(() => this.save());
            } else {
              this.save();
            }
          }
          else {
            scrollIntoInValidInput(this.errors);
          }
        });
      } else {
        this.goNext();
      }
    },
    goNext() {
      this.$emit('onUpdateCurrentStep', stepsEnum.requestApplicantData);
    },
    legalCapacityChange() {
      const categoryId = this.isSecretary ? mainRequestCategoryEnum.deposit : mainRequestCategoryEnum.open;
      this.$set(this.mainRequest, 'categoryId', categoryId);
      this.$set(this.mainRequest, 'requestOwnerTypeId', null);
      this.$set(this.mainRequest, 'procedureTypeId', null);
    },
    requestOwnerTypeChanged() {
      if (this.mainRequest.requestOwnerTypeId) {
        this.$set(this.mainRequest, 'procedureTypeId', null);
      }
    },
    getSelectableProcedureTypes() {
      let types = [];
      if (this.isSecretary) {
        const openStatuesIds = [
          procedureTypeEnum.smallDebtorsLiquidation,
          procedureTypeEnum.smallDebtorsFinancialReorganization,
        ];
        types = this.procedureTypes.filter(p => openStatuesIds.some(op => p.id == op));
      } else {
        switch (this.mainRequest.requestOwnerTypeId) {
          case requestOwnerTypeEnum.debtor:
            types = this.getDebtorProcedureTypes();
            break;
          case requestOwnerTypeEnum.creditor:
            types = this.getCreditorProcedureTypes();
            break;
          case requestOwnerTypeEnum.competentParty:
            types = this.getCompetentPartyProcedureTypes();
            break;
        }
      }
      return types;
    },
    getDebtorProcedureTypes() {
      const openStatuesIds = [
        procedureTypeEnum.preventiveSettlement,
        procedureTypeEnum.financialReorganization,
        procedureTypeEnum.liquidation,
        procedureTypeEnum.administrativeLiquidation,
      ];
      return this.procedureTypes.filter(p => openStatuesIds.some(op => p.id == op));
    },
    getCreditorProcedureTypes() {
      const openStatuesIds = [
        procedureTypeEnum.financialReorganization,
        procedureTypeEnum.smallDebtorsFinancialReorganization,
        procedureTypeEnum.liquidation,
        procedureTypeEnum.smallDebtorsLiquidation,
      ];
      return this.procedureTypes.filter(p => openStatuesIds.some(op => p.id == op));
    },
    getCompetentPartyProcedureTypes() {
      const openStatuesIds = [
        procedureTypeEnum.financialReorganization,
        procedureTypeEnum.liquidation,
        procedureTypeEnum.administrativeLiquidation,
      ];
      return this.procedureTypes.filter(p => openStatuesIds.some(op => p.id == op));
    },
  },
};
</script>

<style lang="scss" scoped>
.error--text {
  font-size: 12px;
}

.pledged {
  ::v-deep .v-input__slot {
    align-items: flex-start;
  }
}

.title-text {
  color: var(--app-dark-primary-color);
  font: normal normal bold 18px/20px Almarai;
  margin-top: 20px;
}
.description-text {
  color: var(--app-lighter-primary-color);
  font: normal normal normal 18px/27px Almarai;
}
.hint-text {
  color: #a7331b;
  font: normal normal normal 18px/27px Almarai;
}
.required-attachments-text {
  color: var(--app-dark-primary-color);
  font: normal normal normal 18px/27px Almarai;
}
.child-list {
  padding-right: 50px;
  padding-top: 10px;
  padding-bottom: 25px;
}
.description-border {
  border-right: var(--app-lighter-primary-color) 2px solid;
}
</style>
